/**
 * MKS-384 — the pending Factory™ questions of one task.
 *
 * Internal-only; the route is session-gated and registered under the `monka`
 * module, so it 404s on an instance without the robot add-on — which is why
 * the hook only mounts for an internal reader on one that has it.
 */
import { apiGet, apiPost } from './client';

export interface PendingTaskQuestion {
  requestId: number;
  /**
   * 1-based position among the task's pending questions, oldest first — the `Q<n>` of a reply.
   * Null (MKS-516) for the rows that are never numbered: a staging review and a rework prompt.
   */
  ordinal: number | null;
  /** The question's title, or its first prompt, clipped server side. Never empty. */
  title: string;
  /** The task comment that carries the question; null when none was written. */
  commentId: number | null;
  /**
   * MKS-516 — the four fields below are optional in the response schema, so an
   * item that carries none of them still parses.
   */
  kind?: 'question' | 'ui_review' | 'rework_scope';
  /** Option buttons to offer; empty when the card is text-only. */
  options?: Array<{ index: number; label: string }>;
  /** Whether a typed reply is a valid answer. */
  allowsText?: boolean;
  /** Whether the signed-in user may answer it (recipient, escalated target or assignee). */
  canAnswer?: boolean;
}

/** GET /api/tasks/:taskId/pending-questions */
export async function getPendingTaskQuestions(taskId: number): Promise<PendingTaskQuestion[]> {
  const res = await apiGet<{ data: PendingTaskQuestion[] }>(`/tasks/${taskId}/pending-questions`);
  return res.data;
}

/** MKS-516 — what the answer route is given. */
export interface AnswerPendingQuestionInput {
  choice: { type: 'option'; index: number } | { type: 'text'; text: string };
  /** "Doesn't look good" only: what should change. Omitted when empty. */
  reworkText?: string;
}

export type AnswerOutcomeStatus =
  | 'accepted'
  | 'already_answered'
  | 'not_pending'
  | 'not_authorized'
  | 'invalid_choice';

/** One outcome of the answer route. */
export interface AnswerOutcomeBase {
  status: AnswerOutcomeStatus;
  answeredByName?: string | null;
  answerChannel?: string | null;
  taskCode?: string | null;
}

export interface AnswerOutcome extends AnswerOutcomeBase {
  /** The outcome of the rework-scope row that a "Doesn't look good" note answered. */
  reworkScope?: AnswerOutcomeBase;
}

/** POST /api/tasks/:taskId/pending-questions/:requestId/answer */
export async function answerPendingTaskQuestion(
  taskId: number,
  requestId: number,
  input: AnswerPendingQuestionInput,
): Promise<AnswerOutcome> {
  const res = await apiPost<{ data: AnswerOutcome }>(
    `/tasks/${taskId}/pending-questions/${requestId}/answer`,
    input,
  );
  return res.data;
}
